/**
 * Created by Jie Gao on 2019-03-29.
 */

import {ReelEvent} from "@nolimitcity/slot-game/bin/core/reel/event/ReelEvent";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {
    IStillImageBackgroundConfig,
    StillImageBackground
} from "@nolimitcity/slot-game/bin/game/background/StillImageBackground";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {DragonTribeGameConfig} from "../DragonTribeGameConfig";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {SpinEvent} from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {TimelineLite, TweenLite, Power4, Power1, TweenMax} from "gsap";
import {DragonTribeGameAssets} from "../DragonTribeGameAssets";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {IGameModeChangedData} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";
import {DragonTribeSoundConfig} from "../DragonTribeSoundConfig";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {DragonTribeParsedGameData} from "../server/data/DragonTribeParsedGameData";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {ScreenEvent} from "@nolimitcity/slot-game/bin/core/screen/event/ScreenEvent";

export class DTBackground extends StillImageBackground {
    private static reelBackgroundTexture:string[] = ["reelBackground", "reelBackgroundFS"];
    private static reelForegroundTexture:string[] = ["reelForeground", "reelForegroundFS"];
    private static dragonTintColors:number[] = [0xae9aaa, 0xbca6b8, 0xd4b7a5, 0xd4744a, 0xfdaa98];
    private _XWays:PIXI.Sprite;
    private _XNudge:PIXI.Sprite;
    private _reelBackground:PIXI.Sprite;
    private _reelForeground:PIXI.Sprite;
    private _foregroundPlants:PIXI.Sprite;
    private _backgroundRock:PIXI.Sprite;
    private _reelInnerGlow:PIXI.Sprite;
    private _reelBgLayer:PIXI.Container;
    private _logoPortrait:PIXI.Sprite;
    private _reelOverGlow:PIXI.Sprite;

    private _dragonFlyTl:TimelineLite = new TimelineLite();
    private _dragonPosY:number[];
    private _screenWidth:number;
    private _reelFire:TimelineSprite;
    private _reelFireActive:TimelineSprite;
    private _flyDragons:TimelineSprite[];
    private _bgSmokeL:TimelineSprite;
    private _bgSmokeR:TimelineSprite;
    private _bgTroch:TimelineSprite;
    private _nearWinFire:TimelineSprite;
    private _playerFSSelection:number;
    private _isLandscape:boolean;
    private _logoInPortrait:boolean;
    private _isRespin:boolean = false;
    private _darkOverlay:PIXI.Graphics;
    private _redOverlay:PIXI.Graphics;

    constructor(config:IStillImageBackgroundConfig) {
        super(config);
        this._dragonPosY = ArrayHelper.initArrayWithValues(6, () => {return 0;});
    }

    protected addEventListeners():void {
        super.addEventListeners();
        EventHandler.addEventListener(this, SpinEvent.BEFORE_START, (event:GameEvent) => this.beforeSpinStart());
        EventHandler.addEventListener(this, ServerEvent.GAME_DATA_PARSED, (event:GameEvent) => this.onGameDataParsed(event.params[0]));
        EventHandler.addEventListener(this, ScreenEvent.GAME_READY, (event:GameEvent) => this.playDragonFlyAnimation());
        EventHandler.addEventListener(this, ReelEvent.ALL_REELS_SPIN_ANIMATION_STOPPED, (event:GameEvent) => this.stopReelInnerGlow());
    }

    protected initAnimations():void {
        super.initAnimations();
        this._reelBgLayer = StageManager.getLayer(DragonTribeGameConfig.instance.LAYERS.REEL_BACKGROUND.name);
        this._redOverlay = new PIXI.Graphics;
        this._redOverlay.visible = false;
        this._reelBgLayer.addChild(this._redOverlay);
        this._flyDragons = ArrayHelper.initArrayWithValues(5, (i:number) => {return this.createDragon(i);});
        this._bgSmokeL = new TimelineSprite(GameResources.getTextures('bgSmoke'));
        this._bgSmokeL.blendMode = PIXI.BLEND_MODES.ADD;
        this._bgSmokeL.alpha = 0.5;
        this._bgSmokeL.scale.set(3.5, 3.5);
        this._bgSmokeL.position.set(-120, 160);
        this._reelBgLayer.addChild(this._bgSmokeL);

        this._darkOverlay = new PIXI.Graphics;
        this._darkOverlay.alpha = 0;
        this._reelBgLayer.addChild(this._darkOverlay);

        this._nearWinFire = new TimelineSprite(GameResources.getTextures('reelFireIdle'));
        this._nearWinFire.blendMode = PIXI.BLEND_MODES.SCREEN;
        this._nearWinFire.anchor.set(0.5, 1);
        this._nearWinFire.position.set(640, 590);
        this._nearWinFire.scale.set(2.3, 3.5);
        this._nearWinFire.hide();
        this._reelBgLayer.addChild(this._nearWinFire);

        this._bgSmokeR = new TimelineSprite(GameResources.getTextures('bgSmoke'));
        this._bgSmokeR.blendMode = PIXI.BLEND_MODES.SCREEN;
        this._bgSmokeR.scale.set(3.5, 3.5);
        this._bgSmokeR.alpha = 0.5;
        this._bgSmokeR.position.set(1000, 200);
        this._reelBgLayer.addChild(this._bgSmokeR);

        this._backgroundRock = new PIXI.Sprite(GameResources.getTextures('rockFloor')[0]);
        this._backgroundRock.anchor.set(0, 0.5);
        this._backgroundRock.position.set(-200, 765);
        this._reelBgLayer.addChild(this._backgroundRock);

        this._bgTroch = new TimelineSprite(GameResources.getTextures('bgTroch'));
        this._bgTroch.blendMode = PIXI.BLEND_MODES.ADD;
        this._bgTroch.position.set(-100, 250);
        this._bgTroch.scale.set(2, 2);
        this._reelBgLayer.addChild(this._bgTroch);

        const foregroundLayer:PIXI.Container = StageManager.getLayer(DragonTribeGameConfig.instance.LAYERS.REEL_FOREROUND.name);
        this._reelBackground = new PIXI.Sprite(GameResources.getTextures('reelBackground')[0]);
        this._reelBackground.position.set(280, 70);
        this._reelBgLayer.addChild(this._reelBackground);

        this._reelInnerGlow = new PIXI.Sprite(GameResources.getTextures('reelInnerGlow')[0]);
        this._reelInnerGlow.blendMode = PIXI.BLEND_MODES.SCREEN;
        this._reelInnerGlow.position.set(280, 70);
        this._reelBgLayer.addChild(this._reelInnerGlow);
        this.stopReelInnerGlow();

        this._reelOverGlow = new PIXI.Sprite(GameResources.getTextures("reelOverGlow")[0]);
        this._reelOverGlow.blendMode = PIXI.BLEND_MODES.SCREEN;
        this._reelOverGlow.scale.set(2);
        this._reelOverGlow.position.set(280, 70);
        foregroundLayer.addChild(this._reelOverGlow);
        this.stopReelOverGlow();

        this._reelFire = new TimelineSprite(GameResources.getTextures('reelFireIdle'));
        this._reelFire.blendMode = PIXI.BLEND_MODES.SCREEN;
        this._reelFire.anchor.set(0.5, 1);
        this._reelFire.position.set(640, 620);
        foregroundLayer.addChild(this._reelFire);
        this._reelFire.playLoop();

        this._reelFireActive = new TimelineSprite(GameResources.getTextures('reelFireActive'));
        this._reelFireActive.blendMode = PIXI.BLEND_MODES.ADD;
        this._reelFireActive.position.set(270, 420);
        foregroundLayer.addChild(this._reelFireActive);
        this._reelFireActive.hide();

        this._reelForeground = new PIXI.Sprite(GameResources.getTextures('reelForeground')[0]);
        this._reelForeground.position.set(34, -190);
        foregroundLayer.addChild(this._reelForeground);

        const riches = new PIXI.Sprite(GameResources.getTextures('riches')[0]);
        riches.position.set(-63, 588);
        foregroundLayer.addChild(riches);

        this._logoPortrait = new PIXI.Sprite(GameResources.getTextures(DragonTribeGameAssets.INTRO_LOGO)[0]);
        this._logoPortrait.anchor.set(0.5, 0);
        this._logoPortrait.position.set(640, -220);
        foregroundLayer.addChild(this._logoPortrait);

        this._XWays = new PIXI.Sprite(GameResources.getTextures("XWays")[0]);
        this._XWays.anchor.set(0.5, 0);
        this._XWays.position.set(400, 20);
        foregroundLayer.addChild(this._XWays);

        this._XNudge = new PIXI.Sprite(GameResources.getTextures("XNudge")[0]);
        this._XNudge.anchor.set(0.5, 0);
        this._XNudge.position.set(880, 20);
        foregroundLayer.addChild(this._XNudge);

        this._foregroundPlants = new PIXI.Sprite(GameResources.getTextures('foreground')[0]);
        this._foregroundPlants.position.set(-200, 760);
        this._foregroundPlants.anchor.set(0, 0.5);
        foregroundLayer.addChild(this._foregroundPlants);
    }

    private onGameDataParsed(data:DragonTribeParsedGameData):void {
        this._isRespin = data.nextMode.indexOf("_AVALANCHE") > -1;
        this._playerFSSelection = data.playerFSSelection;
    }

    private beforeSpinStart():void {
        this.playFireAnimation();
        this.playReelInnerGlow();
    }

    private playReelInnerGlow():void {
        TweenLite.fromTo(this._reelInnerGlow, 0.3,
            {alpha : 0},
            {alpha : 0.8, onComplete : () => this.loopReelInnerGlow()}
        );
        this._reelInnerGlow.visible = true;
    }

    private loopReelInnerGlow():void {
        TweenMax.fromTo(this._reelInnerGlow, 0.6,
            {alpha : 0.8},
            {
                alpha : 1,
                ease : Power1.easeOut,
                repeat : -1,
                yoyo : true,
                yoyoEase : Power1.easeIn
            });
    }

    private stopReelInnerGlow():void {
        TweenLite.to(this._reelInnerGlow, 0.1, {alpha : 0, onComplete : () => this._reelInnerGlow.visible = false});
    }

    private playReelOverGlow():void {
        TweenMax.fromTo(this._reelOverGlow, 0.33,
            {alpha : 0},
            {
                alpha : 1,
                ease : Power1.easeOut,
                repeat : -1,
                yoyo : true,
                yoyoEase : Power1.easeIn
            }
        );
        this._reelInnerGlow.visible = true;
    }

    private stopReelOverGlow():void {
        TweenLite.to(this._reelOverGlow, 0.1, {alpha : 0, onComplete : () => this._reelInnerGlow.visible = false});
    }

    private playFireAnimation():void {
        if(this._isRespin) {
            return;
        }
        new TimelineLite().add([
            () => {SlotGame.sound.playEffect(DragonTribeSoundConfig.SPIN_FIRE);},
            this._reelFireActive.getAnimationAutoShowHide(true, true)
        ]);
    }

    private createDragon(i:number):TimelineSprite {
        const dragon:TimelineSprite = new TimelineSprite(GameResources.getTextures('flyDragon'));
        const dir:boolean = (i % 2 === 0);
        dragon.position.set(1000, 0);
        StageManager.getLayer(DragonTribeGameConfig.instance.LAYERS.DRAGON.name).addChild(dragon);
        dragon.scale.set(dir ? 1 : -1, 1);
        dragon.hide();
        return dragon;
    }

    public playDragonFlyAnimation():void {
        this._flyDragons.forEach((dragon:TimelineSprite) => {
            dragon.playLoop();
        });
        if(!this._dragonFlyTl.isActive()) {
            this._dragonFlyTl.pause();
            this._dragonFlyTl.kill();
            this._dragonFlyTl = new TimelineLite();
            this._flyDragons.forEach((dragon:TimelineSprite, i:number) => {
                const scale:number = Math.random();
                const dir:boolean = (i % 2 === 0);
                dragon.scale.set(dir ? scale : scale * -1, scale);
                dragon.alpha = 0.5 + scale;
                dragon.tint = DTBackground.dragonTintColors[i];
                this._dragonFlyTl.add([
                    () => {
                        dragon.show();
                    },
                    TweenLite.fromTo(dragon, 16 + scale * 4, {x : dir ? -200 : 1600}, {x : dir ? 1600 : -200}),
                    TweenLite.fromTo(dragon, 20, {y : this._dragonPosY[i] + Math.random() * 40}, {
                        y : this._dragonPosY[i] - Math.random() * 40,
                        ease : Power4.easeInOut
                    })
                    // TweenLite.to(dragon.scale, 10 - Math.random() * 4, {x : dir ? scale : scale * -1, y : scale,
                    //     ease : Elastic.easeOut.config(1, 0.3)
                    // })
                ], i * Math.random() * 5);
                this._dragonFlyTl.add(() => {dragon.hide();});
            });
        }
    }

    public playNearWinEffect():void {
        this.playReelOverGlow();
        if(!this._isLandscape) {
            return;
        }
        this._nearWinFire.alpha = 0;
        new TimelineLite().add([() => {this._nearWinFire.show();},
            TweenLite.fromTo(this._darkOverlay, 0.33, {alpha : 0}, {alpha : 1}),
            TweenLite.fromTo(this._nearWinFire, 0.33, {alpha : 0}, {alpha : 1})
        ]).add(() => {this._nearWinFire.playLoop();});
    }

    public stopNearWinEffect():void {
        this.stopReelOverGlow();
        new TimelineLite().add([
            TweenLite.to(this._nearWinFire, 0.33, {alpha : 0}),
            TweenLite.to(this._darkOverlay, 0.33, {alpha : 0})
        ]).add(() => {this._nearWinFire.hide();});
    }

    public showRedOverlay():void {
        this._redOverlay.visible = true;
    }

    public hideRedOverlay():void {
        this._redOverlay.visible = false;
    }

    public showFeatureLogo(playerFSSelection:number):void {
        this._XWays.visible = (playerFSSelection !== 1);
    }

    protected onResize(resizeData:IResizeData):void {
        this._screenWidth = Math.ceil(resizeData.width / this._reelBgLayer.scale.x);
        const screenHeight = Math.ceil(resizeData.height / this._reelBgLayer.scale.y);
        const isLandscape:boolean = (resizeData.orientation === Orientation.LANDSCAPE);
        this._isLandscape = isLandscape;
        this._logoInPortrait = (this._layer.y / this._layer.scale.y > 100);
        if(this._currentMode) {
            this._logoPortrait.visible = (this._logoInPortrait && (this._currentMode.indexOf("FREESPIN") < 0));
        }
        this._dragonPosY = isLandscape ? ArrayHelper.initArrayWithValues(6, (i:number) => {return 50 + i * 50;}) : ArrayHelper.initArrayWithValues(6, (i:number) => {return -280 - i * 50;});
        this._darkOverlay.clear();
        this._darkOverlay.beginFill(0x000000, 0.6);
        this._darkOverlay.drawRect(-this._reelBgLayer.x / this._reelBgLayer.scale.x, -this._reelBgLayer.y / this._reelBgLayer.scale.y - 100, this._screenWidth, screenHeight + 200);
        if(isLandscape) {
            this.playAllSideEffects();
        } else {
            this.hideAllSideEffects();
        }
        this._backgroundSprite.scale.x =(this._screenWidth > 1680)? 1.3:1;

        this._redOverlay.clear();
        this._redOverlay.beginFill(0xe7c7b2, 1);
        this._redOverlay.drawRect(-this._reelBgLayer.x / this._reelBgLayer.scale.x - 300, -this._reelBgLayer.y / this._reelBgLayer.scale.y - 250, this._screenWidth + 400, 250);
    }

    protected onChangeBackground(data:IGameModeChangedData):boolean {
        if(!super.onChangeBackground(data)) {
            return false;
        }
        const textureIndex:number = (data.newMode.indexOf("FREESPIN") > -1 ? 1 : 0);
        this._logoPortrait.visible = (this._logoInPortrait && (data.newMode.indexOf("FREESPIN") < 0));
        this._reelBackground.texture = GameResources.getTextures(DTBackground.reelBackgroundTexture[textureIndex])[0];
        this._reelForeground.texture = GameResources.getTextures(DTBackground.reelForegroundTexture[textureIndex])[0];
        this._backgroundSprite.scale.x =(this._screenWidth > 1680)? 1.3:1;
        return true;
    }

    private playAllSideEffects() {
        this._bgSmokeR.show();
        this._bgSmokeL.show();
        this._bgTroch.show();
        this._bgSmokeR.playLoop();
        this._bgSmokeL.playLoop();
        this._bgTroch.playLoop();
    }

    private hideAllSideEffects() {
        this._bgSmokeR.hide();
        this._bgSmokeL.hide();
        this._bgTroch.hide();
        this._bgSmokeR.stopLoop();
        this._bgSmokeL.stopLoop();
        this._bgTroch.stopLoop();
    }
}